import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useBootstrap, useStoredKeys } from './data.js';
import { useOnline } from './hooks.js';
import { getPlayerId } from './storage.js';
import { finishUpdate } from './update.js';
import SiteHeader from './components/SiteHeader.js';
import JoinGroup from './screens/JoinGroup.js';
import OfflineFirstRun from './screens/OfflineFirstRun.js';
import NamePicker from './screens/NamePicker.js';
import Home from './screens/Home.js';
import SeasonScreen from './screens/SeasonScreen.js';
import SessionDetail from './screens/SessionDetail.js';
import ReminderScreen from './screens/ReminderScreen.js';
import UsageScreen from './screens/UsageScreen.js';
import Settings from './screens/Settings.js';

export default function App() {
  const keys = useStoredKeys();
  const { data, isPending, unauthorized } = useBootstrap(keys);
  const online = useOnline();
  const playerId = getPlayerId();

  // The update curtain comes down when a screen is ready behind it, not when the bundle
  // parses — otherwise it would only uncover "Loading the schedule…" and the second load
  // would still be visible as a second load (§7).
  useEffect(() => {
    if (!isPending) finishUpdate();
  }, [isPending]);

  // No group ID yet, or every stored one has been revoked — both end at the same field (§5).
  if (keys.length === 0) return <JoinGroup reason="first-run" />;
  if (unauthorized) return <JoinGroup reason="rejected" />;
  if (isPending) {
    return (
      <div className="screen center">
        <p className="muted">Loading the schedule…</p>
      </div>
    );
  }
  if (!data) return <OfflineFirstRun />;

  // Rosters are per-season, so a stored id can vanish between years (§5.7).
  const knownPlayer = data.groups.some((g) => g.config.players.some((p) => p.id === playerId));
  if (!playerId || !knownPlayer) return <NamePicker view={data} />;

  // Both halves of the server's check (§5): a group reached through the admin's own group ID,
  // and a roster that says this player is an admin. Picking Jeff's name after typing the ID
  // the players got leaves this false — his schedule, none of the admin screens.
  const isAdmin = data.groups.some(
    (g) => g.admin && g.config.players.some((p) => p.id === playerId && p.isAdmin),
  );

  return (
    <div className="app">
      <SiteHeader isAdmin={isAdmin} online={online} />
      <main className="screen">
        <Routes>
          <Route path="/" element={<Home view={data} playerId={playerId} />} />
          <Route path="/season" element={<SeasonScreen view={data} playerId={playerId} />} />
          <Route
            path="/session/:groupId/:date"
            element={<SessionDetail view={data} playerId={playerId} />}
          />
          <Route
            path="/reminders"
            element={
              isAdmin ? <ReminderScreen view={data} /> : <Navigate to="/" replace />
            }
          />
          {/* Admin-only like the reminder text, and gated the same way: the menu hides the
              link, and a typed-in URL from anyone else lands back on Home. The server refuses
              it too — this is the tidy answer, not the boundary (§5). */}
          <Route
            path="/usage"
            element={isAdmin ? <UsageScreen view={data} /> : <Navigate to="/" replace />}
          />
          <Route path="/settings" element={<Settings view={data} playerId={playerId} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
